import {
  InputType,
  TemplateConditionLink,
  TemplateConditionTermOperator,
  TemplateVariableType,
  VariableNumericDispatch,
} from 'ai-constants'
import {
  ConditionContext,
  InputAnswer,
  InspectionFunction,
  InspectionVariable,
  TemplateConditionTermBaseReference,
  TemplateConditionTerm,
} from 'bff'

type MultiselectOptionAnswer = {
  selected: string
}

export type ConditionDataType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'multiselect'

export const inputsType2datatype: {
  [key in InputType]: ConditionDataType
} = {
  signature: 'string',
  video: 'string',
  audio: 'string',
  check: 'boolean',
  date: 'date',
  file: 'string',
  textarea: 'string',
  text: 'string',
  select: 'string',
  numeric: 'number',
  multiselect: 'multiselect',
  checkboxGroup: 'multiselect',
  dialogDecision: 'boolean',
}

const booleanConditionLiteralValues = {
  true: true,
  false: false,
} as const satisfies Record<'true' | 'false', boolean>

type BooleanConditionLiteral = keyof typeof booleanConditionLiteralValues

const isBooleanConditionLiteral = (
  value: unknown,
): value is BooleanConditionLiteral => value === 'true' || value === 'false'

const keepConditionValue = <TValue>(value: TValue) => value

const conditionValueNormalizers = {
  boolean: <TValue>(value: TValue) =>
    isBooleanConditionLiteral(value)
      ? booleanConditionLiteralValues[value]
      : value,
  string: keepConditionValue,
  number: keepConditionValue,
  date: keepConditionValue,
  multiselect: keepConditionValue,
} as const satisfies Record<
  ConditionDataType,
  <TValue>(value: TValue) => TValue | boolean
>

export const normalizeConditionValue = <TValue>(input: {
  value: TValue
  datatype: ConditionDataType
}): TValue | boolean => conditionValueNormalizers[input.datatype](input.value)

const isMultiselect = (val: unknown): val is MultiselectOptionAnswer[] =>
  Array.isArray(val) &&
  val.every((item) => typeof item === 'object' && 'selected' in item)

export type ConditionOperator = (input: {
  base: unknown
  target: unknown
  datatype: ConditionDataType
}) => boolean

const contains: ConditionOperator = ({ base, target, datatype }) => {
  if (datatype === 'multiselect' && isMultiselect(base)) {
    return base.some((opt) => opt.selected == target)
  }

  if (datatype === 'string' && typeof base === 'string') {
    return base.includes(typeof target === 'string' ? target : '')
  }

  return false
}

const endsWith: ConditionOperator = ({ base, target, datatype }) => {
  if (datatype === 'string' && typeof base === 'string') {
    return base.endsWith(typeof target === 'string' ? target : '')
  }

  return false
}

const greaterThan: ConditionOperator = ({ base, target, datatype }) => {
  if (
    canCompareNumbers({
      base,
      target,
      datatype,
    })
  ) {
    return base > target
  }

  return false
}

const canCompareNumbers: ConditionOperator = ({ base, target, datatype }) => {
  const canCompare =
    (datatype === 'string' || datatype === 'number') &&
    isStringOrNumber(base) &&
    isStringOrNumber(target)

  return canCompare
}

const lessThan: ConditionOperator = ({ base, target, datatype }) => {
  if (
    canCompareNumbers({
      base,
      target,
      datatype,
    })
  ) {
    return base < target
  }

  return false
}

const isStringOrNumber = (val: unknown): val is string | number =>
  typeof val === 'string' || typeof val === 'number'

const startsWith: ConditionOperator = ({ base, target, datatype }) => {
  if (datatype === 'string' && typeof base === 'string') {
    return base.startsWith(typeof target === 'string' ? target : '')
  }

  return false
}

export const numericalOperations: {
  [key in VariableNumericDispatch]: (
    variable: number,
    modifier: number,
  ) => number
} = {
  add: (variable, modifier) => variable + modifier,
  divide: (variable, modifier) => variable / modifier,
  multiply: (variable, modifier) => variable * modifier,
  sub: (variable, modifier) => variable - modifier,
}

export const conditionOperators: {
  [key in TemplateConditionTermOperator]: ConditionOperator
} = {
  startsWith,
  contains,
  endsWith,
  greaterThan,
  lessThan: (input) => lessThan(input),
  notContains: (input) => !contains(input),
  notEndsWith: (input) => !endsWith(input),
  equals: ({ base, target, datatype }) =>
    normalizeConditionValue({ value: base, datatype }) ==
    normalizeConditionValue({ value: target, datatype }),
  isEmpty: ({ base }) => !base,
  isFilled: ({ base }) => !!base,
  notEquals: ({ base, target, datatype }) =>
    normalizeConditionValue({ value: base, datatype }) !=
    normalizeConditionValue({ value: target, datatype }),
  notStartsWith: (input) => !startsWith(input),
}

type InputAnswerExtended = InputAnswer & {
  type: InputType
}

export const variableType2datatype = {
  text: 'string',
  numeric: 'number',
} as const satisfies {
  [key in TemplateVariableType]: 'string' | 'number'
}

export interface VariableExtended extends InspectionVariable {
  type: TemplateVariableType
}

const validationResultDatatype = 'string' as const

const emptyConditionContext: ConditionContext = {
  transformationsByKey: {},
  validationsById: {},
}

const resolveTargetValue = ({
  functions,
  inputs,
  target,
  variables,
}: {
  inputs: InputAnswerExtended[]
  target: NonNullable<TemplateConditionTerm['vars']>[1]
  variables: Array<VariableExtended>
  functions: InspectionFunction[]
}) => {
  switch (target.type) {
    case 'constant':
      return target.value

    case 'function':
      return functions.find((fn) => fn.name === target.value)?.value

    case 'input':
      return inputs.find((input) => input.identifier === target.value)?.value

    case 'variable':
      return variables.find((variable) => variable.name === target.value)?.value
  }

  return
}

const resolveBaseSpecs = ({
  conditionContext,
  functions,
  inputs,
  reference,
  variables,
}: {
  conditionContext: ConditionContext
  inputs: InputAnswerExtended[]
  reference: TemplateConditionTermBaseReference
  variables: Array<VariableExtended>
  functions: InspectionFunction[]
}):
  | {
      value: unknown
      datatype: ConditionDataType
    }
  | undefined => {
  const resolverByReferenceType = {
    function: () => {
      const fn = functions.find((item) => item.name === reference.value)

      return {
        value: fn?.value,
        datatype: inputsType2datatype.numeric,
      }
    },
    input: () => {
      const input = inputs.find((item) => item.identifier === reference.value)

      if (!input) return undefined

      return {
        value: input.value,
        datatype: inputsType2datatype[input.type],
      }
    },
    transformation: () => {
      const transformation =
        conditionContext.transformationsByKey[reference.value]

      if (!transformation) return undefined

      return {
        value: transformation.value,
        datatype: transformation.datatype,
      }
    },
    validation: () => {
      const validation = conditionContext.validationsById[reference.value]

      if (!validation) return undefined

      return {
        value: validation.value,
        datatype: validationResultDatatype,
      }
    },
    variable: () => {
      const variable = variables.find((item) => item.name === reference.value)

      if (!variable) return undefined

      return {
        value: variable.value,
        datatype: variableType2datatype[variable.type],
      }
    },
  } as const satisfies Record<
    TemplateConditionTermBaseReference['type'],
    () =>
      | {
          value: unknown
          datatype: ConditionDataType
        }
      | undefined
  >

  return resolverByReferenceType[reference.type]()
}

const evaluateConditionTerms = ({
  conditionContext = emptyConditionContext,
  inputs,
  term,
  variables = [],
  functions = [],
}: {
  conditionContext?: ConditionContext
  inputs: InputAnswerExtended[]
  term: TemplateConditionTerm
  variables: Array<VariableExtended>
  functions: InspectionFunction[]
}): boolean => {
  if (!term.vars) return false

  const [reference, target] = term.vars

  const referenceSpecs = resolveBaseSpecs({
    conditionContext,
    functions,
    inputs,
    reference,
    variables,
  })

  if (!referenceSpecs) return false

  const targetValue = resolveTargetValue({
    functions,
    inputs,
    target,
    variables,
  })

  return conditionOperators[term.operator]({
    datatype: referenceSpecs.datatype,
    base: referenceSpecs.value,
    target: targetValue,
  })
}

export const meetConditionTerms = ({
  conditionContext = emptyConditionContext,
  inputs,
  link,
  terms,
  variables,
  functions,
}: {
  conditionContext?: ConditionContext
  inputs: InputAnswerExtended[]
  terms: TemplateConditionTerm[]
  link: TemplateConditionLink
  variables: VariableExtended[]
  functions: InspectionFunction[]
}) => {
  const evaluatorByLink = {
    all: () =>
      terms.every((term) =>
        evaluateConditionTerms({
          conditionContext,
          inputs,
          term,
          variables,
          functions,
        }),
      ),
    some: () =>
      terms.some((term) =>
        evaluateConditionTerms({
          conditionContext,
          inputs,
          term,
          variables,
          functions,
        }),
      ),
  } as const satisfies Record<TemplateConditionLink, () => boolean>

  switch (link) {
    case 'all':
      return evaluatorByLink.all()

    case 'some':
      return evaluatorByLink.some()
  }

  return false
}
